import React__default, { createContext, useContext, useRef, useEffect } from 'react';
import { LiveRegion } from '@dnd-kit/accessibility';
import { useToggle } from '@shopify/react-hooks';
import { findFirstFocusableNode } from '@dnd-kit/utilities';
import { useUniqueId } from '../../../../../../../utilities/unique-id/hooks.mjs';
import { Popover as Popover$1 } from '../../../../../../Popover/Popover.mjs';
import { PreventBlur } from '../../../../../components/PreventBlur/PreventBlur.mjs';
import { EditorKeyboardNavigation } from '../../../../../components/EditorKeyboardNavigation/EditorKeyboardNavigation.mjs';

const DEFAULT_CONTEXT = {
  globalPopoverVisibility: false,
  popoverKey: 0,
  hidePopovers: () => {},
  rerenderPopovers: () => {},
  showPopovers: () => {},
  togglePopoverVisibility: () => {}
};
const PopoverContext = /*#__PURE__*/createContext(DEFAULT_CONTEXT);
const ALLOW_FOCUS_SELECTOR = 'input, textarea, select';
function usePopoverContext() {
  return useContext(PopoverContext);
}
function Popover({
  active,
  accessibilityAnnouncement,
  enableExperimentalTabNavigation,
  children,
  ...props
}) {
  const {
    popoverKey,
    globalPopoverVisibility
  } = usePopoverContext();
  const liveRegionId = useUniqueId('Popover-LiveRegion');
  const {
    value: hasFocusWithin,
    setTrue: handleFocus,
    setFalse: handleBlur
  } = useToggle(false);
  const showPopover = hasFocusWithin || active && globalPopoverVisibility;
  const childrenRef = useRef(null);
  useEffect(() => {
    if (!enableExperimentalTabNavigation) {
      return;
    }
    const listener = event => {
      const {
        activeElement
      } = document;
      if (event.key === 'Tab' && childrenRef.current && !childrenRef.current.contains(activeElement)) {
        const firstFocusableNode = findFirstFocusableNode(childrenRef.current);
        if (firstFocusableNode) {
          event.preventDefault();
          handleFocus();
          event.stopImmediatePropagation();
          firstFocusableNode == null ? void 0 : firstFocusableNode.focus();
        }
      }
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [childrenRef, handleFocus, enableExperimentalTabNavigation]);
  return showPopover ? /*#__PURE__*/React__default.createElement(Popover$1, Object.assign({
    key: popoverKey,
    strategy: "prefer-centered-below"
  }, props, {
    onClose: handleBlur
  }), /*#__PURE__*/React__default.createElement(PreventBlur, {
    element: "div",
    selector: "div",
    allowFocusSelector: ALLOW_FOCUS_SELECTOR,
    onClick: stopPropagation,
    onFocus: handleFocus,
    onBlur: handleBlur,
    nodeRef: childrenRef
  }, /*#__PURE__*/React__default.createElement(EditorKeyboardNavigation, null, /*#__PURE__*/React__default.createElement(Popover$1.Card, null, children))), /*#__PURE__*/React__default.createElement(LiveRegion, {
    id: liveRegionId,
    announcement: accessibilityAnnouncement != null ? accessibilityAnnouncement : ''
  })) : null;
}

/**
 * Because the Popover component is typically rendered within
 * Slate nodes, and that those nodes can have `onClick` handlers
 * attached, we want to stop clicks that happen within the popover
 * to propagate to the Slate node handlers.
 */
function stopPropagation(event) {
  event.stopPropagation();
}

export { Popover, PopoverContext, usePopoverContext };
